import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton
} from '@mantine/core'
import { IconBell, IconCheck, IconInfoCircle, IconSparkles, IconAlertTriangle } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { api, type UserNotification } from '../api/client'
import { useAuth } from '../auth/AuthContext'

const PAGE_SIZE = 10

export default function NotificationCenter() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [opened, setOpened] = useState(false)
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<UserNotification[]>([])
  const [total, setTotal] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadUnreadCount = useCallback(async () => {
    if (!profile?.id) return
    try {
      const result = await api.getNotificationUnreadCount()
      setUnreadCount(Math.max(0, Number(result.unreadCount) || 0))
    } catch {
      // The header should remain usable if notification polling fails.
    }
  }, [profile?.id])

  const loadPage = useCallback(async (nextPage: number) => {
    if (!profile?.id) return
    setLoading(true)
    setError(null)
    try {
      const result = await api.listNotifications(PAGE_SIZE, (nextPage - 1) * PAGE_SIZE)
      setItems(result.items || [])
      setTotal(Math.max(0, Number(result.total) || 0))
      setUnreadCount(Math.max(0, Number(result.unreadCount) || 0))
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить уведомления')
    } finally {
      setLoading(false)
    }
  }, [profile?.id])

  useEffect(() => {
    if (!profile?.id) {
      setUnreadCount(0)
      return
    }
    void loadUnreadCount()
    const interval = window.setInterval(() => void loadUnreadCount(), 15_000)
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void loadUnreadCount()
    }
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [profile?.id, loadUnreadCount])

  useEffect(() => {
    if (opened) void loadPage(page)
  }, [opened, page, loadPage])

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount)

  const markRead = async (notification: UserNotification) => {
    if (!notification.read) {
      setItems((current) => current.map((item) => item.id === notification.id ? { ...item, read: true } : item))
      setUnreadCount((current) => Math.max(0, current - 1))
      try {
        await api.markNotificationRead(notification.id)
      } catch {
        void loadPage(page)
      }
    }
    if (notification.actionUrl?.startsWith('/') && !notification.actionUrl.startsWith('//')) {
      setOpened(false)
      navigate(notification.actionUrl)
    }
  }

  const markAllRead = async () => {
    if (unreadCount === 0) return
    setItems((current) => current.map((item) => ({ ...item, read: true })))
    setUnreadCount(0)
    try {
      await api.markAllNotificationsRead()
    } catch {
      void loadPage(page)
    }
  }

  if (!profile?.id) return null

  return (
    <>
      <Box pos="relative">
        <Tooltip label="Уведомления" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={38}
            radius="xl"
            className="header-icon-button"
            aria-label={unreadCount > 0 ? `Уведомления: ${unreadCount} непрочитанных` : 'Уведомления'}
            onClick={() => {
              setPage(1)
              setOpened(true)
            }}
          >
            <IconBell size={20} stroke={1.8} />
          </ActionIcon>
        </Tooltip>
        {unreadCount > 0 && (
          <Box
            component="span"
            aria-hidden="true"
            pos="absolute"
            top={-4}
            right={-5}
            bg="indigo.6"
            c="white"
            fz={10}
            fw={700}
            lh="18px"
            ta="center"
            miw={18}
            h={18}
            px={4}
            style={{ borderRadius: 999, border: '2px solid white', pointerEvents: 'none' }}
          >
            {badgeText}
          </Box>
        )}
      </Box>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<Text fw={700} fz="lg">Уведомления</Text>}
        centered
        size="lg"
        radius="lg"
        overlayProps={{ backgroundOpacity: 0.35, blur: 3 }}
      >
        {unreadCount > 0 && (
          <Group justify="flex-end" mb="md">
            <Button
              variant="subtle"
              color="indigo"
              size="compact-sm"
              leftSection={<IconCheck size={15} />}
              onClick={() => void markAllRead()}
            >
              Прочитать все
            </Button>
          </Group>
        )}

        {loading ? (
          <Center mih={260}><Loader color="indigo" /></Center>
        ) : error ? (
          <Center mih={220}>
            <Stack align="center" gap="xs">
              <Text c="red" ta="center">{error}</Text>
              <Button variant="light" onClick={() => void loadPage(page)}>Повторить</Button>
            </Stack>
          </Center>
        ) : items.length === 0 ? (
          <Center mih={240}>
            <Stack align="center" gap={8}>
              <ThemeIcon size={46} radius="xl" variant="light" color="gray">
                <IconBell size={22} />
              </ThemeIcon>
              <Text fw={600}>Пока уведомлений нет</Text>
              <Text c="dimmed" size="sm">Здесь появятся важные события вашего аккаунта.</Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="sm">
            {items.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => void markRead(notification)}
              />
            ))}
          </Stack>
        )}

        {total > PAGE_SIZE && !loading && !error && (
          <Center mt="lg">
            <Pagination
              total={pageCount}
              value={page}
              onChange={setPage}
              color="indigo"
              radius="xl"
              size="sm"
            />
          </Center>
        )}
      </Modal>
    </>
  )
}

function NotificationItem({ notification, onClick }: { notification: UserNotification; onClick: () => void }) {
  const appearance = useMemo(() => {
    switch (notification.type) {
      case 'SUCCESS':
        return { color: 'teal', icon: IconSparkles }
      case 'WARNING':
        return { color: 'orange', icon: IconAlertTriangle }
      default:
        return { color: 'indigo', icon: IconInfoCircle }
    }
  }, [notification.type])
  const Icon = appearance.icon

  return (
    <UnstyledButton onClick={onClick} style={{ width: '100%', textAlign: 'left' }}>
      <Paper
        p="md"
        radius="md"
        bg={notification.read ? 'white' : 'indigo.0'}
        style={{
          border: notification.read ? '1px solid var(--mantine-color-gray-2)' : '1px solid var(--mantine-color-indigo-2)',
          transition: 'border-color 140ms ease, background 140ms ease'
        }}
      >
        <Group align="flex-start" wrap="nowrap" gap="sm">
          <ThemeIcon color={appearance.color} variant="light" radius="xl" size={36}>
            <Icon size={18} />
          </ThemeIcon>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <Text fw={notification.read ? 600 : 700} size="sm">{notification.title}</Text>
              {!notification.read && <Box w={8} h={8} bg="indigo.6" style={{ borderRadius: 999, flexShrink: 0, marginTop: 5 }} />}
            </Group>
            <Text c="dimmed" size="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
              {notification.message}
            </Text>
            <Group justify="space-between" gap="xs" mt={8}>
              <Text c="dimmed" size="xs">{formatNotificationDate(notification.createdAt)}</Text>
              {notification.actionUrl && <Text c="indigo" size="xs" fw={600}>Открыть</Text>}
            </Group>
          </Box>
        </Group>
      </Paper>
    </UnstyledButton>
  )
}

function formatNotificationDate(raw: string): string {
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  })
}
